import { BrailleTranslationTable, type BrailleTranslationTableJson } from "./BrailleTranslationTable";
import { Language } from "./Language";
import type { LanguageCollection } from "./LanguageCollection";
import { Service, type ServiceJson } from "./Service";

export interface BrailleTranslationServiceJson extends ServiceJson {
  translationTables: Record<string, BrailleTranslationTableJson>;
  defaultTranslationTables:  Record<string, Record<string, string>>;
  defaultDisplayTables: Record<string, string>;
  displayTables: Array<string>;
  hyphenationDictionaries: Record<string, string>;
  selectedTables: Record<string, Record<string, BrailleTranslationTableJson>>;
}

export class BrailleTranslationService extends Service {
  translationTables: Record<string, BrailleTranslationTable>;
  defaultTranslationTables: Record<string, Record<number, BrailleTranslationTable>>;
  defaultDisplayTables: Record<string, string>;
  displayTables: Array<string>;
  hyphenationDictionaries: Record<string, string>;
  selectedTranslationTables: Record<string, Record<number, BrailleTranslationTable>>;


  constructor(json: BrailleTranslationServiceJson, allLanguages: LanguageCollection) {
    super(json, allLanguages);

    this.translationTables = {};
    this.defaultTranslationTables = {};
    this.defaultDisplayTables = {};
    this.displayTables = [];
    this.hyphenationDictionaries = {};
    this.selectedTranslationTables = {};

    for (const [filename, tableJson] of Object.entries(json.translationTables || {})) {
      this.translationTables[filename] = new BrailleTranslationTable(tableJson, allLanguages);
    }

    for (const [languageTag, jsonDotsMap] of Object.entries(json.defaultTranslationTables || {})) {
      let objectDotsMap: Record<number, BrailleTranslationTable> = {};
      this.defaultTranslationTables[languageTag] = objectDotsMap;
      for (const [dotsString, filename] of Object.entries(jsonDotsMap || {})) {
        let dotsNumber = Number.parseInt(dotsString.substring(0,1));
        objectDotsMap[dotsNumber] = this.translationTables[filename]!;
      }
    }
    
    this.defaultDisplayTables = json.defaultDisplayTables;

    this.displayTables = json.displayTables;
    this.hyphenationDictionaries = json.hyphenationDictionaries;

    for (const [languageTag, jsonSortMap] of Object.entries(json.selectedTables || {})) {
      let objectSortMap: Record<number, BrailleTranslationTable> = {};
      this.selectedTranslationTables[languageTag] = objectSortMap;
      for (const [sortString, tableJson] of Object.entries(jsonSortMap || {})) {
        let sortNumber = Number.parseInt(sortString);
        objectSortMap[sortNumber] = new BrailleTranslationTable(tableJson, allLanguages);
      }
    }
  }

  /**
   * Returns the default translation table for the given language and dot count.
   * Unless opted out, a fallback for a different dot cound will be returned if
   * no default for the requested count exists.
   * @param language The language for which the default table is requested. Only the primary
   * tag is used for the lookup.
   * @param dots Number of dots, only 6 and 8 can match.
   * @param dotsFallback true by default, determines if a table with any dot cound can will
   * be returned if no default for the requested count exists.
   * @returns a single BrailleTranslationTable, or undefined.
   */
  getDefaultTranslationTableForLanguage(
    language: Language,
    dots: number, 
    dotsFallback: boolean = true,
    useSelectedTables: boolean = false
  ): BrailleTranslationTable | undefined {
    let ret: BrailleTranslationTable | undefined = undefined;

    if (useSelectedTables) {
      let sortMap = this.selectedTranslationTables[language.getPrimary()];
      if (sortMap) {
        ret = sortMap[0];
        if (ret?.dots != dots && !dotsFallback) {
          ret = undefined;
        }
      }
    } else {
      let dotsMap = this.defaultTranslationTables[language.getPrimary()];
      if (dotsMap) {
        ret = dotsMap[dots];
        if (!ret && dotsFallback) {
          ret = Object.values(dotsMap)[0];
        }
      }
    }
    return ret; 
  }

  /**
   * Returns the filename of the display table for the given language, if one is defined.
   * @param language The language for which the default table is requested. Only the primary
   * tag is used for the lookup.
   * @returns a filename or undefined (that is, will never return an empty string)
   */
  getDefaultDisplayTableForLanguage(language: Language): string | undefined {
    return this.defaultDisplayTables[language.getPrimary()];
  }

  /**
   * Returns the default table(s) for the given language and dot count, if defined.
   * Unless opted out, a fallback for a different dot cound will be returned if
   * no default for the requested count exists.
   * @param language The language for which the default table is requested. Only the primary
   * tag is used for the lookup.
   * @param dots Number of dots, only 6 and 8 can match. Only affects the translation table 
   * (e.g. not the display table.)
   * @param dotsFallback true by default, determines if a translation table with any dot cound can will
   * be returned if no default for the requested count exists.
   * @returns if defined, returns a "tables string", which is a comma-separated list of one or
   * more filenames. Usually, this will be exactly one display table filename followed by
   * exactly one translation table filename, but other counts are possible. If no defaults
   * for the given language are defined, returns undefined (that is, will never return an empty string).
   */
  getDefaultTablesForLanguage(
    language: Language,
    dots: number, 
    dotsFallback: boolean = true,
    useSelectedTables: boolean = false,
  ): string | undefined {
    const displayTable = this.getDefaultDisplayTableForLanguage(language);
    const translationTable = this.getDefaultTranslationTableForLanguage(language, dots, dotsFallback, useSelectedTables);
    if (displayTable && translationTable) {
      return displayTable + "," + translationTable.filename;
    }
    return undefined;
  }

  /**
   * Returns the known tables for a given language. Either the old data or new data ("selectedTables") is used. Data is never combined from both sources.
   * @param language Only the primary language tag is compared.
   * @param useSelectedTables whether to use the old data (false, default) or new data (true).
   * @returns 
   */
  getTranslationTablesForLanguage(language: Language, useSelectedTables: boolean = false): Array<BrailleTranslationTable> {
    if (useSelectedTables) {
      return Object.values(this.selectedTranslationTables[language.getPrimary()] || {});
    } else {
      return Object.values(this.translationTables).filter(table => Language.equalPrimaries(table.language, language));
    }
  }

  /**
   * Gets all tables from the new data ("selectedTables") but as flat array. Note that the array is re-computed each time
   * you call this method, even though the result is always equal. Cache the result instead of calling it many times.
   * @returns 
   */
  getAllSelectedTranslationTables(): Array<BrailleTranslationTable> {
    let allTables: Array<BrailleTranslationTable> = [];
    for (const language of Object.keys(this.selectedTranslationTables)) {
      let tablesForLanguage = this.selectedTranslationTables[language]!;
      allTables.push(...Object.values(tablesForLanguage));
    }
    return allTables;
  }

  /**
   * Returns a single TranslationTable object for the given filename, if found, or undefined if not found.
   * 
   * Note that the old data contains much more tables than the new one, but even for the tables that
   * are contained in both data souces, the actual data is different. So even if you request data with
   * useFallback = true, it matters what primary data source you select. 
   * @param filename 
   * @param useSelectedTables whether to use the old data (false, default) or new data (true).
   * @param useFallback whether to use the other datasource as fallback if the primary data source did not yield a result.
   * @returns 
   */
  getTranslationTableByFilename(filename: string, useSelectedTables: boolean = false, useFallback: boolean = true): BrailleTranslationTable | undefined {
    if (useSelectedTables) {
      if (useFallback) {
        return this.getSelectedTranslationTableByFilename(filename) || this.getClassicTranslationTableByFilename(filename);
      } else {
        return this.getSelectedTranslationTableByFilename(filename);
      }
    } else {
      if (useFallback) {
        return this.getClassicTranslationTableByFilename(filename) || this.getSelectedTranslationTableByFilename(filename);
      } else {
        return this.getClassicTranslationTableByFilename(filename);
      }
    }
  }

  /**
   * Returns a single TranslationTable object for the given filename, if found, or undefined if not found. Only uses the old data source.
   * @param filename 
   * @returns 
   */
  getClassicTranslationTableByFilename(filename: string): BrailleTranslationTable | undefined {
    return this.translationTables[filename];
  }

  /**
   *  Returns a single TranslationTable object for the given filename, if found, or undefined if not found. Only uses the new data source.
   * @param filename 
   * @returns 
   */
  getSelectedTranslationTableByFilename(filename: string): BrailleTranslationTable | undefined {
    for (const language of Object.keys(this.selectedTranslationTables)) {
      let tablesForLanguage = Object.values(this.selectedTranslationTables[language]!);
      let foundTable = tablesForLanguage.find(table => table.filename == filename);
      if (foundTable) {
        return foundTable;
      }
    }
    return undefined;
  }

  hasDisplayTable(filename: string): boolean {
    return this.displayTables.includes(filename);
  }

  hasHyphenationDictionary(filename: string): boolean {
    return Object.keys(this.hyphenationDictionaries).includes(filename);
  }

  encodingForHyphenationDictionary(filename: string): string {
    return this.hyphenationDictionaries[filename]!;
  }
}
